/// <reference types="google.maps" />
import { useEffect, useState } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import MapLoadGuard, { useMapRetryKey } from "@/components/MapLoadGuard";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Maximize2, MapPin } from "lucide-react";
import { inlineMapGestures, type MapGestures } from "@/lib/mapGestures";

interface MapLocationPickerProps {
  lat: number;
  lng: number;
  onChange: (lat: number, lng: number) => void;
  /** Height class for the inline preview map. Defaults to "h-64". */
  className?: string;
}

interface LatLng {
  lat: number;
  lng: number;
}

// Imperatively recenter the map on its pin only when `token` changes, used to
// resync the inline map after the larger-map modal closes. We deliberately do
// NOT recenter on every pin change, so clicking / dragging to set a pin never
// yanks the camera out from under the user.
const RecenterOnToken = ({ pin, token }: { pin: LatLng; token: number }) => {
  const map = useMap();
  useEffect(() => {
    if (map) map.panTo(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, token]);
  return null;
};

// A single Google Map whose CAMERA is fully decoupled from the PIN. The camera
// is uncontrolled, the user pans/zooms freely with `greedy` gestures and the
// map never snaps back, while the marker is driven by `pin`. Clicking the map
// or dragging the marker reports new coordinates via `onPick` but never moves
// the camera. `disableDefaultUI` + `zoomControl` keeps a clean, app-like map
// (only the +/- buttons), and `clickableIcons={false}` means tapping a POI sets
// the pin instead of opening Google's place popup.
const PinPickerMap = ({
  pin,
  defaultZoom,
  mapId,
  onPick,
  recenterToken,
  gestures = "greedy",
}: {
  pin: LatLng;
  defaultZoom: number;
  mapId: string;
  onPick: (lat: number, lng: number) => void;
  recenterToken: number;
  gestures?: MapGestures;
}) => {
  return (
    <Map
      defaultCenter={pin}
      defaultZoom={defaultZoom}
      onClick={(e) => {
        const c = e.detail.latLng;
        if (c) onPick(c.lat, c.lng);
      }}
      gestureHandling={gestures}
      disableDefaultUI
      zoomControl
      clickableIcons={false}
      mapId={mapId}
    >
      <AdvancedMarker
        position={pin}
        draggable
        onDragEnd={(e: google.maps.MapMouseEvent) => {
          const c = e.latLng;
          if (c) onPick(c.lat(), c.lng());
        }}
      />
      <RecenterOnToken pin={pin} token={recenterToken} />
    </Map>
  );
};

// Reusable Google Maps picker. Same prop interface as the previous
// MapLibre implementation so consumers don't change. Two surfaces:
//   * inline preview (compact, click/drag to set)
//   * "Open larger map" → fullscreen modal (precise picking)
// Both fire onChange(lat, lng) on every click / drag-end.
const MapLocationPicker = ({
  lat,
  lng,
  onChange,
  className = "h-64",
}: MapLocationPickerProps) => {
  const [open, setOpen] = useState(false);
  const pin = { lat, lng };
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || "";
  // On flaky connections the Maps script can fail to load, MapLoadGuard shows
  // a fallback and "Try again" remounts the provider (key) to re-run the loader.
  const { key: retryKey, retry, onLoad, ready, retrying } = useMapRetryKey();

  return (
    <APIProvider key={retryKey} apiKey={apiKey} libraries={["marker"]} onLoad={onLoad}>
    <div className="space-y-2">
      <div className="relative">
        <div className={`w-full ${className} rounded-xl overflow-hidden border`}>
          <MapLoadGuard retry={retry} ready={ready} retrying={retrying}>
            <PinPickerMap
              pin={pin}
              defaultZoom={13}
              mapId="msth-location-picker"
              onPick={onChange}
              gestures={inlineMapGestures()}
              // Re-sync the inline map to the pin whenever the modal toggles, so a
              // pick made in the larger map is centered when we return here.
              recenterToken={open ? 1 : 0}
            />
          </MapLoadGuard>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setOpen(true)}
          className="absolute top-3 right-3 gap-1.5 shadow-md"
        >
          <Maximize2 className="h-3.5 w-3.5" /> Open larger map
        </Button>
      </div>
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <MapPin className="h-3 w-3" /> {lat.toFixed(5)}, {lng.toFixed(5)}
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          phoneSheet={false}
          overlayClassName="z-[120]"
          className="z-[120] max-w-5xl w-[95vw] max-h-[92dvh] p-0 gap-0 overflow-hidden flex flex-col"
        >
          <DialogHeader className="shrink-0 px-6 pt-5 pb-2 pr-10 text-left">
            <DialogTitle>Pin your property location</DialogTitle>
            <DialogDescription>
              Click anywhere on the map or drag the marker to set the exact spot.
            </DialogDescription>
          </DialogHeader>
          <div className="w-full flex-1 min-h-[40dvh] h-[55dvh] sm:h-[70vh]">
            {/* The modal map re-mounts each time the dialog opens, so it always
                starts centered on the current pin. */}
            <MapLoadGuard retry={retry} ready={ready} retrying={retrying}>
              <PinPickerMap
                pin={pin}
                defaultZoom={15}
                mapId="msth-location-picker-expanded"
                onPick={onChange}
                recenterToken={0}
              />
            </MapLoadGuard>
          </div>
          <div className="shrink-0 px-6 py-3 flex items-center justify-between gap-3 border-t bg-muted/30">
            <p className="text-xs text-muted-foreground">
              Coordinates:{" "}
              <span className="font-mono">
                {lat.toFixed(5)}, {lng.toFixed(5)}
              </span>
            </p>
            <Button type="button" onClick={() => setOpen(false)} size="sm">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </APIProvider>
  );
};

export default MapLocationPicker;
